import { keepPreviousData } from '@tanstack/react-query';
import { Terminal } from 'lucide-react';
import { trpc } from '~/api/trpc';
import { Alert, AlertDescription, AlertTitle } from '~/components/ui/alert';
import { Button } from '~/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '~/components/ui/dialog';
import { LogsTable } from '~/pages/Logs';

type BillingDraftLogsModalProps = {
    billingDraftId: string;
    open: boolean;
    onClose: () => void;
};

export default function BillingDraftLogsModal({ billingDraftId, open, onClose }: BillingDraftLogsModalProps) {
    const logsQuery = trpc.logs.getLogs.useQuery(
        {
            page: 1,
            pageSize: 100,
            billingDraftId,
        },
        {
            placeholderData: keepPreviousData,
            enabled: open === true,
        }
    );

    const logs = (logsQuery.data?.logs ?? []).filter((log) => log.billingDraftId === billingDraftId);

    return (
        <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>Auftrag-Logs</DialogTitle>
                    <DialogDescription>
                        Alle System-Logs für diesen Auftrag (ID: <span className="font-mono">{billingDraftId}</span>)
                    </DialogDescription>
                </DialogHeader>
                <div className="p-4">
                    {logsQuery.isError && (
                        <Alert variant="destructive" className="mb-6">
                            <Terminal className="h-4 w-4" />
                            <AlertTitle>Fehler beim Laden der Logs</AlertTitle>
                            <AlertDescription>
                                {logsQuery.error?.message ?? 'Ein unbekannter Fehler ist aufgetreten.'}
                            </AlertDescription>
                        </Alert>
                    )}
                    <div className="rounded-md border bg-muted/50 p-2 max-h-[50vh] overflow-auto">
                        <LogsTable
                            logs={logs}
                            isLoading={logsQuery.isLoading}
                            itemsPerPage={100}
                        />
                        {logs.length === 0 && !logsQuery.isLoading && (
                            <div className="text-center text-muted-foreground py-8">
                                Keine Logs für diesen Auftrag gefunden.
                            </div>
                        )}
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        Schließen
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
